/**
 * 极简紫韵模版 - 导航栏
 * 
 * 设计理念：玻璃拟态半透明 + 紫色品牌渐变条
 * 与sunset（暖色渐变条）、green（通用Navbar）都不同：
 * - 顶部细条品牌色（紫色渐变）
 * - 毛玻璃白色导航栏
 * - 桌面端：Logo左 + 导航中 + 发布按钮右
 * - 移动端：汉堡菜单
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSite } from '@/context/SiteContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { useTemplate } from '@/context/TemplateContext';
import { getNavCategories, getNavMenus, type FrontendCategory } from '@/utils/api';

export default function PurpleNavbar() {
  const navigate = useNavigate();
  const { isLoggedIn, logout, user } = useAuth();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();
  const { themeColor } = useTemplate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [navMenus, setNavMenus] = useState<any[]>([]);
  const [navCategories, setNavCategories] = useState<FrontendCategory[]>([]);

  const siteName = siteConfig?.site_name || siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const enableVip = siteConfig?.enable_vip !== false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getNavMenus().then(setNavMenus).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* 品牌色细条 */}
      <div className="h-1 bg-gradient-to-r from-purple-500 via-violet-500 to-fuchsia-500" />

      {/* 主导航 */}
      <nav
        className={`transition-all duration-300 ${
          scrolled
            ? 'bg-white/85 backdrop-blur-xl shadow-lg shadow-purple-500/5 border-b border-purple-100/50'
            : 'bg-white/95 backdrop-blur-md border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0">
              {logo ? (
                <img src={logo} alt={siteName} className="h-9" />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-violet-600 to-fuchsia-500 flex items-center justify-center text-white font-bold text-lg shadow-md shadow-purple-500/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'P'}
                </div>
              )}
              {siteName && (
                <span className="text-lg font-bold text-gray-800 hidden sm:block">{siteName}</span>
              )}
            </Link>

            {/* 桌面端导航 */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors"
              >
                首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : (menu.url || '#')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  {menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* 右侧操作区 */}
            <div className="flex items-center gap-3">
              {/* 发布按钮 */}
              <Link
                to="/publish"
                className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-purple-500 via-violet-600 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:from-purple-600 hover:via-violet-700 hover:to-fuchsia-600 transition-all shadow-md shadow-purple-500/20"
              >
                <i className="ri-add-line" />发布
              </Link>

              {/* 用户菜单 */}
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center text-white text-sm font-bold shadow-sm"
                  >
                    {user?.nickname?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl border border-purple-100 py-2 z-20">
                        <div className="px-4 py-3 border-b border-gray-50">
                          <div className="text-sm font-semibold text-gray-800">{user?.nickname || user?.username}</div>
                          <div className="text-xs text-gray-400 mt-0.5">会员用户</div>
                        </div>
                        <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-purple-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <i className="ri-user-line text-purple-400" />会员中心
                        </Link>
                        <Link to="/dashboard/my-listings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-purple-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <i className="ri-file-list-line text-purple-400" />我的发布
                        </Link>
                        {enableVip && (
                          <Link to="/vip" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-purple-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                            <i className="ri-vip-crown-line text-purple-400" />VIP特权
                          </Link>
                        )}
                        <hr className="my-1 border-gray-50" />
                        <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors">
                          <i className="ri-logout-box-line" />退出登录
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="hidden md:block text-sm font-medium text-purple-600 hover:text-purple-700 px-3 py-2 rounded-xl hover:bg-purple-50 transition-colors"
                  >
                    登录 / 注册
                  </Link>
                </>
              )}

              {/* 移动端汉堡 */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-purple-50 transition-colors"
              >
                <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-gray-700`} />
              </button>
            </div>
          </div>
        </div>

        {/* 移动端菜单 */}
        {mobileOpen && (
          <div className="md:hidden border-t border-purple-50 bg-white/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-1">
              <Link to="/" className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-purple-50 rounded-xl" onClick={() => setMobileOpen(false)}>首页</Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : (menu.url || '#')}
                  className="block px-4 py-3 text-sm text-gray-600 hover:bg-purple-50 rounded-xl"
                  onClick={() => setMobileOpen(false)}
                >{menu.name}</Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="block px-4 py-3 text-sm text-gray-600 hover:bg-purple-50 rounded-xl"
                  onClick={() => setMobileOpen(false)}
                >{cat.name}</Link>
              ))}
              <hr className="my-2 border-gray-50" />
              <Link to="/publish" className="flex items-center justify-center gap-2 mx-4 py-3 bg-gradient-to-r from-purple-500 to-violet-600 text-white text-sm font-bold rounded-xl" onClick={() => setMobileOpen(false)}>
                <i className="ri-add-line" />发布信息
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
